/*
 * flashmeeting-fe
 *
 * Author: Andgel BRISSAUD
 * Copyright (c) 2024 Prodeo - All Rights Reserved
 */

/* ===== External Libraries ===== */
import { AXIOS, CallbackErrorAxios, CallbackWarningAxios, CommonGlobalService } from '@umanforge/common';
import { AxiosResponse, AxiosError } from 'axios';
import { Dispatch } from '@reduxjs/toolkit';

/* ===== API Configuration ===== */
import { API } from 'constants/api.constant';

/* ===== Types and Interfaces ===== */
import { eKpiSpec } from 'enum/kpiSpec.enum';
import { EngagementRateStats, EngagementRaw, GlobalKPI } from 'interfaces/kpi.interface';
import { WeeklyEngagementRate } from 'interfaces/typeCharts.interface';

/* ===== Services ===== */
import { GlobalService } from './global.service';
import { EngagementDetail } from 'interfaces/types.interface';

export class KPIService {

  /* ===== Helper method to build route ===== */
  private static buildRoute( basePath: string, spec: number, id: string ): string {
    switch ( spec ) {
    case eKpiSpec.EVENT_MANAGER:
      return `${basePath}/${spec}/event-manager/${id}`;
    case eKpiSpec.SECTOR:
      return `${basePath}/${spec}/sector/${id}`;
    case eKpiSpec.SECTORS:
      return `${basePath}/${spec}/sectors/${id}`;
    case eKpiSpec.SITE:
      return `${basePath}/${spec}/site/${id}`;
    case eKpiSpec.SITES:
      return `${basePath}/${spec}/sites/${id}`;
    default:
      throw new Error( 'Invalid spec value' );
    }
  }

  /* ===== Static methods ===== */
  static getWeeklyEngagementRate(
    spec: number, id: string,
    token: string | null, dispatch: Dispatch,
    onError?: CallbackErrorAxios, onWarning?: CallbackWarningAxios
  ): Promise<WeeklyEngagementRate> {
    return new Promise( async resolve => {
      const route = this.buildRoute( `${API.KPI}/weekly-engagement-rate`, spec, id );
      AXIOS.get( route, await CommonGlobalService.getHeaderRequest( token ) )
        .then( ( response: AxiosResponse<WeeklyEngagementRate> ) => {
          CommonGlobalService.saveRequestToken( response, dispatch );
          if ( response.status === 202 ) {
            GlobalService.onAxiosWarning( response, onWarning );
          }
          resolve( response.data );
        } )
        .catch( ( err: AxiosError ) => {
          GlobalService.onAxiosError( err, dispatch, onError );
        } );
    } );
  }

  static getRecurringEngagementPerWeek(
    spec: number, id: string,
    token: string | null, dispatch: Dispatch,
    onError?: CallbackErrorAxios, onWarning?: CallbackWarningAxios
  ): Promise<WeeklyEngagementRate> {
    return new Promise( async resolve => {
      const route = this.buildRoute( `${API.KPI}/recurring-engagement-per-week`, spec, id );
      AXIOS.get( route, await CommonGlobalService.getHeaderRequest( token ) )
        .then( ( response: AxiosResponse<WeeklyEngagementRate> ) => {
          CommonGlobalService.saveRequestToken( response, dispatch );
          if ( response.status === 202 ) {
            GlobalService.onAxiosWarning( response, onWarning );
          }
          resolve( response.data );
        } )
        .catch( ( err: AxiosError ) => {
          GlobalService.onAxiosError( err, dispatch, onError );
        } );
    } );
  }

  static getRecurringEngagementPerTheme(
    spec: number, id: string,
    token: string | null, dispatch: Dispatch,
    onError?: CallbackErrorAxios, onWarning?: CallbackWarningAxios
  ): Promise<WeeklyEngagementRate> {
    return new Promise( async resolve => {
      const route = this.buildRoute( `${API.KPI}/recurring-engagement-per-theme`, spec, id );
      AXIOS.get( route, await CommonGlobalService.getHeaderRequest( token ) )
        .then( ( response: AxiosResponse<WeeklyEngagementRate> ) => {
          CommonGlobalService.saveRequestToken( response, dispatch );
          if ( response.status === 202 ) {
            GlobalService.onAxiosWarning( response, onWarning );
          }
          resolve( response.data );
        } )
        .catch( ( err: AxiosError ) => {
          GlobalService.onAxiosError( err, dispatch, onError );
        } );
    } );
  }

  static getLateEngagementPerTheme(
    spec: number, id: string,
    token: string | null, dispatch: Dispatch,
    onError?: CallbackErrorAxios, onWarning?: CallbackWarningAxios
  ): Promise<WeeklyEngagementRate> {
    return new Promise( async resolve => {
      const route = this.buildRoute( `${API.KPI}/late-engagement-per-theme`, spec, id );
      AXIOS.get( route, await CommonGlobalService.getHeaderRequest( token ) )
        .then( ( response: AxiosResponse<WeeklyEngagementRate> ) => {
          CommonGlobalService.saveRequestToken( response, dispatch );
          if ( response.status === 202 ) {
            GlobalService.onAxiosWarning( response, onWarning );
          }
          resolve( response.data );
        } )
        .catch( ( err: AxiosError ) => {
          GlobalService.onAxiosError( err, dispatch, onError );
        } );
    } );
  }

  static getLateEngagementPerRange(
    spec: number, id: string,
    token: string | null, dispatch: Dispatch,
    onError?: CallbackErrorAxios, onWarning?: CallbackWarningAxios
  ): Promise<WeeklyEngagementRate> {
    return new Promise( async resolve => {
      const route = this.buildRoute( `${API.KPI}/late-engagement-per-range`, spec, id );
      AXIOS.get( route, await CommonGlobalService.getHeaderRequest( token ) )
        .then( ( response: AxiosResponse<WeeklyEngagementRate> ) => {
          CommonGlobalService.saveRequestToken( response, dispatch );
          if ( response.status === 202 ) {
            GlobalService.onAxiosWarning( response, onWarning );
          }
          resolve( response.data );
        } )
        .catch( ( err: AxiosError ) => {
          GlobalService.onAxiosError( err, dispatch, onError );
        } );
    } );
  }

  static getGlobalKPI(
    spec: number, id: string,
    token: string | null, dispatch: Dispatch,
    onError?: CallbackErrorAxios, onWarning?: CallbackWarningAxios
  ): Promise<GlobalKPI> {
    return new Promise( async resolve => {
      const route = this.buildRoute( `${API.KPI}/global-kpi`, spec, id );
      AXIOS.get( route, await CommonGlobalService.getHeaderRequest( token ) )
        .then( ( response: AxiosResponse<GlobalKPI> ) => {
          CommonGlobalService.saveRequestToken( response, dispatch );
          if ( response.status === 202 ) {
            GlobalService.onAxiosWarning( response, onWarning );
          }
          resolve( response.data );
        } )
        .catch( ( err: AxiosError ) => {
          GlobalService.onAxiosError( err, dispatch, onError );
        } );
    } );
  }

  static getLateEngagementDetailsByTheme(
    themeId: string, spec: number, id: string,
    token: string | null, dispatch: Dispatch,
    onError?: CallbackErrorAxios, onWarning?: CallbackWarningAxios
  ): Promise<EngagementDetail[]> {
    return new Promise( async resolve => {
      const basePath = `${API.KPI}/late-engagement-per-theme/${themeId}`;
      const route = this.buildRoute( basePath, spec, id );

      AXIOS.get( route, await CommonGlobalService.getHeaderRequest( token ) )
        .then( ( response: AxiosResponse<{ engagements: EngagementRaw[] }> ) => {
          CommonGlobalService.saveRequestToken( response, dispatch );
          if ( response.status === 202 ) {
            GlobalService.onAxiosWarning( response, onWarning );
          }
          const engagementDetails: EngagementDetail[] = response.data.engagements.map( ( engagement: EngagementRaw ) =>
          {return KPIService.createEngagementDetail( engagement );}
          );
          resolve( engagementDetails );
        } )
        .catch( ( err: AxiosError ) => {
          GlobalService.onAxiosError( err, dispatch, onError );
        } );
    } );
  }

  static getLateEngagementPerRangeByRange(
    range: string, spec: number, id: string,
    token: string | null, dispatch: Dispatch,
    onError?: CallbackErrorAxios, onWarning?: CallbackWarningAxios
  ): Promise<EngagementDetail[]> {
    return new Promise( async resolve => {
      const basePath = `${API.KPI}/late-engagement-per-range/${range}`;
      const route = this.buildRoute( basePath, spec, id );

      AXIOS.get( route, await CommonGlobalService.getHeaderRequest( token ) )
        .then( ( response: AxiosResponse<{ engagements: EngagementRaw[] }> ) => {
          CommonGlobalService.saveRequestToken( response, dispatch );
          if ( response.status === 202 ) {
            GlobalService.onAxiosWarning( response, onWarning );
          }
          const engagementDetails: EngagementDetail[] = response.data.engagements.map( ( engagement: EngagementRaw ) =>
          {return KPIService.createEngagementDetail( engagement );}
          );
          resolve( engagementDetails );
        } )
        .catch( ( err: AxiosError ) => {
          GlobalService.onAxiosError( err, dispatch, onError );
        } );
    } );
  }

  static getRecurringEngagementPerThemeByTheme(
    themeId: string, spec: number, id: string,
    token: string | null, dispatch: Dispatch,
    onError?: CallbackErrorAxios, onWarning?: CallbackWarningAxios
  ): Promise<EngagementDetail[]> {
    return new Promise( async resolve => {
      const basePath = `${API.KPI}/recurring-engagement-per-theme/${themeId}`;
      const route = this.buildRoute( basePath, spec, id );

      AXIOS.get( route, await CommonGlobalService.getHeaderRequest( token ) )
        .then( ( response: AxiosResponse<{ engagements: EngagementRaw[] }> ) => {
          CommonGlobalService.saveRequestToken( response, dispatch );
          if ( response.status === 202 ) {
            GlobalService.onAxiosWarning( response, onWarning );
          }
          const engagementDetails: EngagementDetail[] = response.data.engagements.map( ( engagement: EngagementRaw ) =>
          {return KPIService.createEngagementDetail( engagement );}
          );
          resolve( engagementDetails );
        } )
        .catch( ( err: AxiosError ) => {
          GlobalService.onAxiosError( err, dispatch, onError );
        } );
    } );
  }

  static getRecurringEngagementPerWeekByWeek(
    week: string, spec: number, id: string,
    token: string | null, dispatch: Dispatch,
    onError?: CallbackErrorAxios, onWarning?: CallbackWarningAxios
  ): Promise<EngagementDetail[]> {
    return new Promise( async resolve => {
      const basePath = `${API.KPI}/recurring-engagement-per-week/${week}`;
      const route = this.buildRoute( basePath, spec, id );

      AXIOS.get( route, await CommonGlobalService.getHeaderRequest( token ) )
        .then( ( response: AxiosResponse<{ engagements: EngagementRaw[] }> ) => {
          CommonGlobalService.saveRequestToken( response, dispatch );
          if ( response.status === 202 ) {
            GlobalService.onAxiosWarning( response, onWarning );
          }
          const engagementDetails: EngagementDetail[] = response.data.engagements.map( ( engagement: EngagementRaw ) =>
          {return KPIService.createEngagementDetail( engagement );}
          );
          resolve( engagementDetails );
        } )
        .catch( ( err: AxiosError ) => {
          GlobalService.onAxiosError( err, dispatch, onError );
        } );
    } );
  }

  static createEngagementDetail(
    engagement: EngagementRaw,
  ): EngagementDetail {
    return {
      sites: [],
      tables: [],
      theme: engagement.themeName,
      title: engagement.title,
      createdAt: engagement.createdDate,
      createdBy: engagement.createdBy,
      note: engagement.note,
      deliveryDate: engagement.deliveryDate,
      participants: engagement.Participants,
      what: engagement.what,
      when: engagement.when,
      where: engagement.where,
      who: engagement.who,
      why: engagement.why,
      isDone: engagement.completed === 1,
      endDate: engagement.completedDate,
      isArchived: engagement.archived === 1,
      archiveDate: engagement.archivedDate,
      updatedAt: engagement.updatedDate,
      id: engagement.id,
      eventId: engagement.eventId,
    };
  }

  static getEngagementRateStats(
    spec: number, id: string,
    token: string | null, dispatch: Dispatch,
    onError?: CallbackErrorAxios, onWarning?: CallbackWarningAxios
  ): Promise<EngagementRateStats> {
    return new Promise( async resolve => {
      const route = this.buildRoute( `${API.KPI}/engagement-rate-stats`, spec, id );
      AXIOS.get( route, await CommonGlobalService.getHeaderRequest( token ) )
        .then( ( response: AxiosResponse<EngagementRateStats> ) => {
          CommonGlobalService.saveRequestToken( response, dispatch );
          if ( response.status === 202 ) {
            GlobalService.onAxiosWarning( response, onWarning );
          }
          resolve( response.data );
        } )
        .catch( ( err: AxiosError ) => {
          GlobalService.onAxiosError( err, dispatch, onError );
        } );
    } );
  }

}
